/**
 * useChatThreadRealtime
 *
 * Keeps an open chat thread fresh with lightweight realtime refreshes.
 *
 * Primary responsibilities:
 * - Subscribe to chat and message row changes for one chat
 * - Refresh on focus, online, and visibility recovery
 * - Coalesce repeated refresh triggers while the user stays on the thread
 *
 * Dependencies:
 * - lib/realtime/private-channel-auth
 * - lib/supabase/client
 */

'use client'

import { useEffect, useRef, useState } from 'react'
import type {
  RealtimeChannel,
  RealtimePostgresChangesPayload
} from '@supabase/supabase-js'

import { syncPrivateRealtimeAuth } from '@/lib/realtime/private-channel-auth'
import {
  getRealtimeReconnectDelay,
  isRealtimeChannelFailure,
  removeRealtimeChannelOnce,
  shouldRemoveRealtimeChannel
} from '@/lib/realtime/channel-recovery'
import { createClient } from '@/lib/supabase/client'
import { safeConsole } from '@/lib/utils/safeConsole'
import { Tables } from '@/supabase/types'

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export type ChatThreadMessageRealtimeEvent =
  | {
      eventType: 'INSERT' | 'UPDATE'
      message: Tables<'messages'>
    }
  | {
      eventType: 'DELETE'
      chatId: string | null
      messageId: string | null
      sequenceNumber: number | null
    }

interface UseChatThreadRealtimeOptions {
  chatId: string | null
  enabled?: boolean
  onThreadChange: () => void
  onMessageEvent?: (event: ChatThreadMessageRealtimeEvent) => void
}

// --------------

function normalizeChatId(value: string | null | undefined) {
  const normalized = value?.trim()

  return normalized && UUID_PATTERN.test(normalized) ? normalized : null
}

// --------------

function canSubscribeToThreadRealtime() {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden'
}

// --------------

export function useChatThreadRealtime({
  chatId,
  enabled = true,
  onThreadChange,
  onMessageEvent
}: UseChatThreadRealtimeOptions) {
  const callbackRef = useRef(onThreadChange)
  const messageEventRef = useRef(onMessageEvent)
  const reconnectAttemptRef = useRef(0)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const visibleBackfillRef = useRef(false)
  const [canSubscribeToRealtime, setCanSubscribeToRealtime] = useState(
    canSubscribeToThreadRealtime
  )
  const [reconnectNonce, setReconnectNonce] = useState(0)

  useEffect(() => {
    callbackRef.current = onThreadChange
  }, [onThreadChange])

  useEffect(() => {
    messageEventRef.current = onMessageEvent
  }, [onMessageEvent])

  useEffect(() => {
    const normalizedChatId = normalizeChatId(chatId)

    if (!enabled || !normalizedChatId) {
      return
    }

    const supabase = createClient()
    const channels: RealtimeChannel[] = []
    let isDisposed = false

    const clearReconnectTimer = () => {
      if (!reconnectTimeoutRef.current) {
        return
      }

      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    const scheduleReconnect = () => {
      if (isDisposed || reconnectTimeoutRef.current) {
        return
      }

      const delayMs = getRealtimeReconnectDelay(reconnectAttemptRef.current)
      reconnectAttemptRef.current += 1
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null

        if (!isDisposed) {
          setReconnectNonce(value => value + 1)
        }
      }, delayMs)
    }

    const refreshThread = () => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden'
      ) {
        return
      }

      callbackRef.current()
    }

    const queueRefresh = () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }

      refreshTimeoutRef.current = setTimeout(() => {
        refreshTimeoutRef.current = null
        refreshThread()
      }, 200)
    }

    const subscribeWithRefresh = (channel: RealtimeChannel) => {
      channels.push(channel)

      void syncPrivateRealtimeAuth(supabase)
        .then((isReady) => {
          if (!isReady || isDisposed) {
            if (!isReady && !isDisposed) {
              queueRefresh()
              scheduleReconnect()
            }

            return
          }

          channel.subscribe((status) => {
            if (status === 'SUBSCRIBED') {
              reconnectAttemptRef.current = 0
              clearReconnectTimer()
              queueRefresh()
              return
            }

            if (isRealtimeChannelFailure(status)) {
              if (!isDisposed) {
                safeConsole.warn('Chat thread realtime channel failed', {
                  chatId: normalizedChatId,
                  status
                })
                queueRefresh()
                if (shouldRemoveRealtimeChannel(status)) {
                  void removeRealtimeChannelOnce(supabase, channel)
                }
                scheduleReconnect()
              }
            }
          })
        })
        .catch((error) => {
          if (!isDisposed) {
            safeConsole.warn('Chat thread realtime auth sync failed', error)
            queueRefresh()
            scheduleReconnect()
          }
        })
    }

    const handleMessagePayload = (
      payload: RealtimePostgresChangesPayload<Tables<'messages'>>
    ) => {
      const onRealtimeMessageEvent = messageEventRef.current

      if (!onRealtimeMessageEvent) {
        queueRefresh()
        return
      }

      if (payload.eventType === 'DELETE') {
        onRealtimeMessageEvent({
          eventType: 'DELETE',
          chatId: payload.old.chat_id ?? null,
          messageId: payload.old.id ?? null,
          sequenceNumber:
            typeof payload.old.sequence_number === 'number'
              ? payload.old.sequence_number
              : null
        })
        queueRefresh()
        return
      }

      onRealtimeMessageEvent({
        eventType: payload.eventType,
        message: payload.new as Tables<'messages'>
      })
      queueRefresh()
    }

    const handleRealtimeRecovery = () => {
      const isVisible = canSubscribeToThreadRealtime()

      if (isVisible && !canSubscribeToRealtime) {
        visibleBackfillRef.current = true
      }

      if (isVisible && canSubscribeToRealtime) {
        queueRefresh()
      }

      setCanSubscribeToRealtime(isVisible)
    }

    window.addEventListener('focus', handleRealtimeRecovery)
    window.addEventListener('online', handleRealtimeRecovery)
    document.addEventListener('visibilitychange', handleRealtimeRecovery)

    if (canSubscribeToRealtime) {
      subscribeWithRefresh(
        supabase
          .channel(`chat-thread:${normalizedChatId}`, {
            config: {
              private: true
            }
          })
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'messages',
              filter: `chat_id=eq.${normalizedChatId}`
            },
            handleMessagePayload
          )
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'chats',
              filter: `id=eq.${normalizedChatId}`
            },
            queueRefresh
          )
      )

      if (visibleBackfillRef.current) {
        visibleBackfillRef.current = false
        queueRefresh()
      }
    }

    return () => {
      isDisposed = true

      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
        refreshTimeoutRef.current = null
      }

      clearReconnectTimer()

      window.removeEventListener('focus', handleRealtimeRecovery)
      window.removeEventListener('online', handleRealtimeRecovery)
      document.removeEventListener('visibilitychange', handleRealtimeRecovery)

      channels.forEach((channel) => {
        void removeRealtimeChannelOnce(supabase, channel)
      })
    }
  }, [canSubscribeToRealtime, chatId, enabled, reconnectNonce])
}
